import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import CompanyModel from '@mm/state/model/CompanyModel';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import { useDispatch, useSelector } from 'react-redux';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import * as allPermissions from '@mm/constants/facebook-permissions';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { ErrorReportingUtils } from '@mm/plugins';
import { Dispatch } from 'redux';
import { StoreState } from '@mm/state/store-interfaces';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';

interface Opts {
  permissions?: string;
  askForPermissions?: boolean;
  allowManualFlow?: boolean;
  onSuccess?: (page: ConnectedPageModel) => void;
  otherCompany?: CompanyModel;
  otherFacebookPage?: EnhancedBillingPage;
}

export const useConnectPageHandlers = (
  {
    allowManualFlow,
    askForPermissions,
    permissions = '',
    onSuccess = () => {},
    otherFacebookPage,
  }: Opts = {} as Opts,
) => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const activeCompany = useSelector(activeCompanySelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const modalActions = useModalActions();
  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();
  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const websiteFormatted = activeFacebookPage?.name;

  const createSession = async ({ facebookToken }) => {
    await facebookSessionCRUDActions.create({
      facebookToken,
      notFbUser: true,
    });
    if (activeCompany) {
      modalActions.openConnectPagesModal({
        title: `Connect Facebook Page to ${websiteFormatted || 'your website'}`,
        destinationPageId: otherFacebookPage
          ? otherFacebookPage?.id
          : activeFacebookPage?.id,
        company: activeCompany as CompanyModel,
        canSelectMultiplePages: false,
        onSuccess: page => {
          if (page || otherFacebookPage) {
            const facebookPage = otherFacebookPage ? otherFacebookPage : page;
            connectedPageCRUDActions.retrievePage(facebookPage.id);
            onSuccess(facebookPage);
          }
        },
      });
    }
  };

  const connect = async () => {
    const fbPermissionsObjects = askForPermissions
      ? allPermissions[permissions]
      : allPermissions.ALL_PERMISSIONS;

    const fbPermissions =
      allPermissions.getArrayOfPermissions(fbPermissionsObjects);

    try {
      const res = allowManualFlow
        ? await dispatch(
            facebookSDKActions.requestPermissionsWithManualFlowFallback(
              fbPermissions,
            ),
          )
        : await dispatch(facebookSDKActions.requestPermissions(fbPermissions));
      if (res) {
        await createSession(res);
      }
    } catch (error) {
      ErrorReportingUtils.reportError(
        `use-connect-page-handlers: login failure`,
        {
          extraData: { permissionsRequested: fbPermissions, error },
        },
      );
      return error;
    }
  };

  return {
    connect,
  };
};
